import { Plus, RefreshCcw } from 'lucide-react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView } from '../../../../types/entity-types';
import { getTimeSinceLastSyncFromAccounts } from '../../../transactions/utils/get-time-since-last-sync';
import { AccountsTable } from '../../components/accounts-table';

type ConnectedAccountsPageProps = {
  openBankingAccounts: AccountView[];
  modulrAccounts: AccountView[];
  demoAccounts: AccountView[];
  shouldShowConnectDemoDataEURButton: boolean;
  shouldShowConnectDemoDataGBPButton: boolean;
  shouldShowGmailConnectorButton: boolean;
  isSyncingAccounts: boolean;
  onSyncAccounts: () => void;
  onLinkAccount: () => void;
  onLinkEmailAccount: () => void;
  onGenerateDemoDataEUR: () => void;
  onGenerateDemoDataGBP: () => void;
  onDeleteAccount: (accountId: string) => Promise<void>;
};

type ConnectedAccountsByTypeProps = {
  accounts: AccountView[];
  onSyncAccounts?: () => void;
  isSyncingAccounts?: boolean;
  onDisconnectAccount?: (accountId: string) => Promise<void>;
  onLinkAccount?: () => void;
};

const DemoAccountsSection = ({ accounts, onDisconnectAccount }: ConnectedAccountsByTypeProps) => {
  return (
    <Stack align="stretch" space={3}>
      <h1 className="text-sm font-medium text-black">Demo Accounts</h1>
      <AccountsTable accounts={accounts} showOptionsButton={true} onDisconnectAccount={onDisconnectAccount} />
    </Stack>
  );
};

const SeapointAccountsSection = ({ accounts }: ConnectedAccountsByTypeProps) => {
  return (
    <Stack align="stretch" space={3}>
      <h1 className="text-sm font-medium text-black">Seapoint Accounts</h1>
      <AccountsTable accounts={accounts} showOptionsButton={false} />
    </Stack>
  );
};

const OpenBankingAccountsSection = ({
  accounts,
  onSyncAccounts,
  isSyncingAccounts,
  onDisconnectAccount,
  onLinkAccount,
}: ConnectedAccountsByTypeProps) => {
  const lastSyncData = getTimeSinceLastSyncFromAccounts(accounts || []);
  const hasOpenBankingAccounts = accounts.length > 0;

  if (!hasOpenBankingAccounts) {
    return (
      <Stack align="stretch" space={2}>
        <Inline justify="between" align="end" className="mb-2">
          <h1 className="text-sm font-medium text-black">Open Banking Accounts</h1>
        </Inline>

        <Button
          variant="ghost"
          iconLeft={
            <Plus
              strokeWidth={1.5}
              className="rounded-full bg-seapoint-gray-background-modal p-1 group-hover:bg-white"
              size={32}
            />
          }
          onClick={onLinkAccount}
          data-testid="link-open-banking-account-button-empty"
          className="p-y-2 p-x-3 ml-0.5 w-fit gap-3 rounded-[10px] text-sm font-medium hover:bg-seapoint-gray-background-hover"
        >
          Link Account
        </Button>
      </Stack>
    );
  }

  return (
    <Stack align="stretch" space={1}>
      <Inline justify="between" align="end" className="mb-2">
        <h1 className="text-sm font-medium text-black">Open Banking Accounts</h1>

        <Button
          variant="secondary"
          onClick={onSyncAccounts}
          iconLeft={<RefreshCcw size={14} />}
          isLoading={isSyncingAccounts}
          className="text-seapoint-gray-text"
        >
          {lastSyncData?.text}
        </Button>
      </Inline>
      <AccountsTable
        accounts={accounts}
        showOptionsButton={true}
        onDisconnectAccount={onDisconnectAccount}
        className="gap-0 py-0"
      />
      <Button
        variant="ghost"
        iconLeft={
          <Plus
            strokeWidth={1.5}
            className="rounded-full bg-seapoint-gray-background-modal p-1 group-hover:bg-white"
            size={32}
          />
        }
        onClick={onLinkAccount}
        className="p-y-2 p-x-3 ml-0.5 w-fit gap-3 rounded-[10px] text-sm font-medium hover:bg-seapoint-gray-background-hover"
      >
        Link Account
      </Button>
    </Stack>
  );
};

export const ConnectedAccountsPage = ({
  openBankingAccounts,
  modulrAccounts,
  demoAccounts,
  shouldShowConnectDemoDataEURButton,
  shouldShowConnectDemoDataGBPButton,
  shouldShowGmailConnectorButton,
  onGenerateDemoDataEUR,
  onGenerateDemoDataGBP,
  onLinkAccount,
  isSyncingAccounts,
  onSyncAccounts,
  onLinkEmailAccount,
  onDeleteAccount,
}: ConnectedAccountsPageProps) => {
  return (
    <Stack align="stretch" space={4} className="bg-white px-32 pb-5">
      <Inline justify="between" className="h-[42px]">
        <h1 className="text-2xl font-semibold text-black">Accounts</h1>

        <Inline className="gap-y-3">
          {(shouldShowConnectDemoDataEURButton || shouldShowConnectDemoDataGBPButton) && (
            <Inline className="gap-2">
              {shouldShowConnectDemoDataEURButton && (
                <Button variant="default" onClick={onGenerateDemoDataEUR}>
                  Generate demo data EUR
                </Button>
              )}
              {shouldShowConnectDemoDataGBPButton && (
                <Button variant="default" onClick={onGenerateDemoDataGBP}>
                  Generate demo data GBP
                </Button>
              )}
            </Inline>
          )}

          {shouldShowGmailConnectorButton && (
            <Button onClick={onLinkEmailAccount} variant="secondary">
              Link email account
            </Button>
          )}
        </Inline>
      </Inline>
      <Stack space={5} align="stretch" className="mt-5">
        {modulrAccounts.length > 0 && <SeapointAccountsSection accounts={modulrAccounts} />}
        {demoAccounts.length > 0 && (
          <DemoAccountsSection accounts={demoAccounts} onDisconnectAccount={onDeleteAccount} />
        )}

        <OpenBankingAccountsSection
          accounts={openBankingAccounts}
          onSyncAccounts={onSyncAccounts}
          isSyncingAccounts={isSyncingAccounts}
          onDisconnectAccount={onDeleteAccount}
          onLinkAccount={onLinkAccount}
        />
      </Stack>
    </Stack>
  );
};
